// Copyright (c) Hamilton Manufacturing Corp.
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { between } from '../_common/api/utility';
import Button from '../_common/form/Button';
import Heading from '../_common/form/Heading';
import Input from '../_common/form/Input';
import Select from '../_common/form/Select';
import Title from '../_common/form/Title';
import Modal from '../_common/Modal';
import Page from '../_ecom/pageLayout/Page';
import InputUpdater from './SiteConfig/InputUpdater';
import { defaultSiteConfig } from '../../InitialState';
import { HamQuest } from '../../App';

const Section = styled.div`
    display: flex;
    flex-direction: column;

    margin: 1em 0;
`

const Admin = () => {
    const [accounts, setAccounts] = useState([]);
    const [HSAccount, setHS] = useState(null);
    const [subdomain, setSubdomain] = useState('');
    const [defaultConfig, setDefaultConfig] = useState(defaultSiteConfig);
    const logoUrl = 'https://hamiltonprodcdn.blob.core.windows.net/web/SUB/logo.svg'

    useEffect(() => {
        HamQuest.get('/accounts')
            .then(res => setAccounts(res))
    }, [])

    const createSite = () => {
        Modal.Confirmation({
            title: 'Are you sure?',
            body: `Are you sure you want to create a site for ${subdomain}?`,
            type: 'warning',
            hideClose: true,
            closeAfter: true,
        })
            .then(res => {
                if (!res) return;
                Modal.Promise({
                    title: 'Creating',
                    callback: HamQuest.post('/accounts', {
                        hsAccountId: HSAccount,
                        subdomain: subdomain,
                        siteConfig: defaultConfig
                    })
                })
                    .then(() =>
                        Modal.Show({
                            title: 'Success',
                            body: `${subdomain} created successfully!`,
                            type: 'success',
                        })
                    )
                    .catch(ex =>
                        Modal.Show({
                            type: 'error',
                            title: 'Error',
                            body: ex.blob || 'Something went wrong. Please try again.'
                        })
                    )
            })
    }

    return (
        <Page>
            <Section>
                <Title icon='list'>Admin</Title>
                {
                    accounts.length ? (
                        <Select
                            standalone
                            value={HSAccount || ''}
                            onChange={e => setHS(e.target.value)}
                            label='HS Account'
                        >
                            <option value={''}>HS Account</option>
                            {accounts.sort((a, b) => b.hsAccountId - a.hsAccountId).map((e, i) => <option key={`option-${i}`} value={e.hsAccountId}>{e.hsAccountId} - {e.companyName}</option>)}
                        </Select>
                    ) : <span>Loading HS Accounts...</span>
                }
                <Input
                    standalone
                    label='Subdomain'
                    value={subdomain}
                    onChange={(e) => {
                        setSubdomain(e.target.value)
                        if (defaultSiteConfig.logo.match(/hamiltonprodcdn/)) {
                            setDefaultConfig({
                                ...defaultConfig,
                                logo: logoUrl.replace('SUB', e.target.value),
                            })
                        }
                    }}
                />
                <Button
                    disabled={!HSAccount || !subdomain}
                    onClick={createSite}
                >Create Site</Button>
            </Section>

            <Section>
                <Heading>Details</Heading>
                <InputUpdater
                    label='Company Name'
                    default={defaultConfig.companyName}
                    onUpdate={(value, config) => config.companyName = value}
                    configUpdater={setDefaultConfig}
                    initialConfig={defaultConfig}
                />
                <InputUpdater
                    label='Company Phone'
                    default={defaultConfig.contact.phone}
                    onUpdate={(value, config) => config.contact.phone = value}
                    type='number'
                    configUpdater={setDefaultConfig}
                    initialConfig={defaultConfig}
                />
                <InputUpdater
                    label='Company Logo'
                    default={defaultConfig.logo.replace('SUB', subdomain)}
                    onUpdate={(value, config) => config.logo = value}
                    configUpdater={setDefaultConfig}
                    initialConfig={defaultConfig}
                />
                <InputUpdater
                    label='Value Card Name'
                    default={defaultConfig.cardName}
                    onUpdate={(value, config) => config.cardName = value}
                    configUpdater={setDefaultConfig}
                    initialConfig={defaultConfig}
                />
                <InputUpdater
                    checkbox
                    label='Has App'
                    default={defaultConfig.has_app}
                    onUpdate={(value, config) => config.has_app = value}
                    configUpdater={setDefaultConfig}
                    initialConfig={defaultConfig}
                />
                <InputUpdater
                    checkbox
                    label='Has Ecom'
                    default={defaultConfig.has_ecom}
                    onUpdate={(value, config) => config.has_ecom = value}
                    configUpdater={setDefaultConfig}
                    initialConfig={defaultConfig}
                />
            </Section>
            <Section>
                <Heading>Google Maps</Heading>
                <InputUpdater
                    label='Google Zoom'
                    default={defaultConfig.googleMap.zoom}
                    onUpdate={(value, config) => config.googleMap.zoom = value}
                    type='number'
                    configUpdater={setDefaultConfig}
                    initialConfig={defaultConfig}
                />
                <InputUpdater
                    label='Google Center Lat'
                    default={defaultConfig.googleMap.center.lat}
                    onUpdate={(value, config) => config.googleMap.center.lat = value}
                    type='number'
                    configUpdater={setDefaultConfig}
                    initialConfig={defaultConfig}
                />
                <InputUpdater
                    label='Google Center Lng'
                    default={defaultConfig.googleMap.center.lng}
                    onUpdate={(value, config) => config.googleMap.center.lng = value}
                    type='number'
                    configUpdater={setDefaultConfig}
                    initialConfig={defaultConfig}
                />
            </Section>

            <Section>
                <Heading>PWA</Heading>
                <InputUpdater
                    checkbox
                    label='Bubbles'
                    default={defaultConfig.bubbles.enabled}
                    onUpdate={(value, config) => config.bubbles.enabled = value}
                    configUpdater={setDefaultConfig}
                    initialConfig={defaultConfig}
                />
                <InputUpdater
                    label='Number of Bubbles'
                    default={defaultConfig.bubbles.number}
                    onUpdate={(value, config) => config.bubbles.number = between(value, 1, 20)}
                    type='number'
                    configUpdater={setDefaultConfig}
                    initialConfig={defaultConfig}
                />
                <InputUpdater
                    checkbox
                    label='Force Location Select'
                    default={defaultConfig.forceLocations}
                    onUpdate={(value, config) => config.forceLocations = value}
                    type='number'
                    configUpdater={setDefaultConfig}
                    initialConfig={defaultConfig}
                />
                <InputUpdater
                    checkbox
                    label='Image Parallax'
                    default={defaultConfig.imgParallax}
                    onUpdate={(value, config) => config.imgParallax = value}
                    configUpdater={setDefaultConfig}
                    initialConfig={defaultConfig}
                />
                <InputUpdater
                    checkbox
                    label='Loyalty Program'
                    default={defaultConfig.loyaltyProgram}
                    onUpdate={(value, config) => config.loyaltyProgram = value}
                    configUpdater={setDefaultConfig}
                    initialConfig={defaultConfig}
                />
            </Section>


        </Page >
    );
}

export default Admin;
